<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'show-notification'])

const authStore = useAuthStore()

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const fechamentos = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const usuariosNomes = reactive({})

const temPermissaoCompleta = computed(() => authStore.pode('FECHAMENTO_DE_PERIODOS_COMPLETO'))

const colunas = [
  { field: 'idFechamentoPeriodo', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'dataFechada', label: 'Data Fechada (Até)', sortable: true, slotName: 'dataFechada' },
  { field: 'tsLancamento', label: 'Lançado Em', sortable: true, slotName: 'tsLancamento' },
  { field: 'usuarioId', label: 'Lançado Por (Usuário)', sortable: true, slotName: 'usuario' },
  { field: 'isAtivo', label: 'Status', width: 150, align: 'center', sortable: true, slotName: 'status' }
]

const sortResolver = (field) => {
  if (!field.startsWith('lst')) return null
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    buscarDados(1)
  } else {
    fechamentos.value = []
    paginacao.value.TotalCount = 0
  }
})

const carregarNomesUsuarios = async (dados) => {
  const idsUnicos = [...new Set(dados.map(d => d.usuarioId).filter(id => id && !isNaN(id)))]
  const promessas = idsUnicos.map(async (id) => {
    if (!usuariosNomes[id]) {
      try {
        const res = await api.get(`/Usuarios/${id}`)
        usuariosNomes[id] = res.data.nome
      } catch (e) {
        usuariosNomes[id] = 'Desconhecido'
      }
    }
  })
  await Promise.all(promessas)
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    fechamentos.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/FechamentosPeriodosFinanceiros?${params.toString()}`)
    fechamentos.value = response.data

    await carregarNomesUsuarios(response.data)

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os históricos de fechamento.'
    fechamentos.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

onMounted(() => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Fechamento de Períodos"
    subtitulo="Histórico de bloqueio fiscal e transacional. Esta ação é imutável."
    :filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">{{ erro }}</div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="fechamentos"
      :loading="loading"
      empty-message="Nenhum fechamento registrado."
      row-key="idFechamentoPeriodo"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idFechamentoPeriodo }}</span>
      </template>
      <template #dataFechada="{ row }">
        <span class="fw-bold">
          <i class="bi bi-lock-fill me-2 text-warning" v-if="row.isAtivo"></i>
          {{ formatarDataBR(row.dataFechada) }}
        </span>
      </template>

      <template #tsLancamento="{ row }">
        {{ formatarDataHoraBR(row.tsLancamento) }}
      </template>

      <template #usuario="{ row }">
        <span class="text-body-secondary small">
          <i class="bi bi-person-fill me-1"></i>
          <span v-if="row.usuarioId === 'Sistema'">Sistema</span>
          <span v-else>({{ row.usuarioId }}) {{ usuariosNomes[row.usuarioId] || '...' }}</span>
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="row.isAtivo ? 'badge-ghost-success' : 'badge-ghost-danger'">
          {{ row.isAtivo ? 'Ativo' : 'Inativo' }}
        </span>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<style scoped>
</style>
